import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { fimEfetivo } from "@nhomi/core";
import { useSession } from "@/lib/auth";
import { watchMyChallenges, type Challenge } from "@/data/challenges";
import { dayKey } from "@/data/plates";
import { entregarFotos, fotosCompartilhadas } from "@/lib/compartilhado";
import { Empty, Spinner, TopBar } from "@/ui/primitives";
import { IconChevron } from "@/ui/icons";
import { Marca } from "./Home";

/* ============================================================
   "Compartilhar com o Nhomi"

   A tela que nasce quando alguém segura a foto no WhatsApp ou na galeria
   e escolhe o Nhomi. O caminho inteiro — quem entrega, quem atende o
   POST, por que passa por cache — está em `lib/compartilhado.ts`.

   Ela existe por UMA pergunta que o Android não tem como responder: em
   qual desafio. O compartilhamento entrega uma foto, não um grupo, e
   escolher sozinho seria publicar no lugar errado — que é pior que
   perguntar, porque o prato já está no placar de outra gente quando a
   pessoa percebe.

   Quando a pergunta só tem uma resposta possível — um desafio rolando —
   ela não é feita: a tela salta direto pro formulário. É o caso da
   maioria, e é o pedido original: "só pra eu não ter trabalho de
   procurar o app, abrir, clicar em desafio, clicar em mais".

   O `replace` na navegação é o que faz o voltar do Android sair do app em
   vez de cair aqui de novo — sem ele, quem desiste do formulário volta
   pra esta tela, escolhe o mesmo desafio e não entende por que voltou.
   ============================================================ */

export function Compartilhar() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  /* `null` é "ainda lendo", `[]` é "li e não veio nada" — as duas coisas
     desenham telas diferentes, e um estado só as confundiria. */
  const [fotos, setFotos] = useState<File[] | null>(null);
  const [desafios, setDesafios] = useState<Challenge[] | null>(null);
  const [previas, setPrevias] = useState<string[]>([]);

  useEffect(() => {
    let vivo = true;
    void fotosCompartilhadas().then((f) => { if (vivo) setFotos(f); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    if (!user) return;
    return watchMyChallenges(user.uid, setDesafios);
  }, [user]);

  /* Quem cria também revoga. As prévias morrem ao sair da tela: o
     `Compose` faz as dele a partir da foto já reduzida. */
  useEffect(() => {
    if (!fotos?.length) return;
    const urls = fotos.map((f) => URL.createObjectURL(f));
    setPrevias(urls);
    return () => { for (const u of urls) URL.revokeObjectURL(u); };
  }, [fotos]);

  /* Só os que estão rolando HOJE. Desafio que terminou não recebe prato,
     e desafio que não começou também não — oferecer os dois seria
     oferecer um caminho que o formulário recusa no fim. */
  const hoje = dayKey();
  const abertos = useMemo(
    () => (desafios ?? [])
      .filter((d) => d.startDay <= hoje && hoje <= fimEfetivo(d.endDay))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [desafios, hoje],
  );

  /* Sem `replace` aqui de propósito: quem escolheu o desafio errado volta
     pra esta tela com o voltar do Android e escolhe outro. A foto
     continua na mão — a leitura do cache é memorizada por abertura, e
     devolve as mesmas fotos na volta. */
  function escolher(cid: string) {
    entregarFotos(fotos ?? []);
    navigate(`/c/${cid}/novo`);
  }

  /* Um desafio só: não há pergunta a fazer. O salto espera as DUAS
     respostas — a foto e a lista —, senão ele aconteceria com a lista
     ainda vazia do cache do Firestore e levaria a lugar nenhum. */
  const unico = abertos.length === 1 ? abertos[0] : null;
  useEffect(() => {
    if (!fotos?.length || !unico) return;
    entregarFotos(fotos);
    navigate(`/c/${unico.id}/novo`, { replace: true });
  }, [fotos, unico, navigate]);

  if (fotos === null) {
    return (
      <main className="screen center">
        <Spinner label="Recebendo a foto" />
      </main>
    );
  }

  /* A foto não chegou. Acontece de verdade em dois casos, e os dois têm
     a mesma saída: o app foi instalado e nunca aberto, então não existe
     service worker pra atender o POST; ou esta tela foi recarregada
     depois de a foto já ter sido lida. */
  if (!fotos.length) {
    return (
      <main className="screen">
        <TopBar title="Compartilhar" back="/" />
        <Empty emoji="🤔" title="Não achei a foto">
          <p>
            {params.has("vazio")
              ? "O compartilhamento chegou sem imagem nenhuma."
              : "Talvez esta tela já tenha sido aberta antes com ela."}{" "}
            Tente compartilhar de novo — ou publique pela câmera do app,
            que dá no mesmo.
          </p>
        </Empty>
        <div className="pad">
          <Link className="btn btn-primary" to="/">Ir pros meus desafios</Link>
        </div>
      </main>
    );
  }

  if (desafios === null) {
    return (
      <main className="screen center">
        <Spinner label="Abrindo seus desafios" />
      </main>
    );
  }

  if (!abertos.length) {
    return (
      <main className="screen">
        <TopBar title="Compartilhar" back="/" />
        <Empty emoji="🍽️" title="Nenhum desafio rolando">
          <p>
            A foto chegou, mas não há desafio seu acontecendo hoje pra
            receber ela. Crie um, ou entre num com o código de convite.
          </p>
        </Empty>
        <div className="pad">
          <Link className="btn btn-primary" to="/novo">Criar desafio</Link>
          <Link className="btn btn-outline" to="/entrar-codigo">Entrar com um código</Link>
        </div>
      </main>
    );
  }

  /* Com um desafio só a tela nem chega a aparecer — o efeito acima já
     navegou. Este `return` cobre o piscar de um quadro entre as duas
     coisas, e some sozinho. */
  if (unico) {
    return (
      <main className="screen center">
        <Spinner label="Abrindo o desafio" />
      </main>
    );
  }

  return (
    <main className="screen">
      <TopBar title="Compartilhar" back="/" />
      <h1 className="page-title">Postar em qual desafio?</h1>

      {/* A foto aparece porque ela é a prova de que chegou a certa. Quem
          compartilha da galeria pode ter tocado na miniatura errada, e
          descobrir isso aqui custa um toque em vez de um prato publicado. */}
      <div className="compartilhado-previa">
        {previas.slice(0, 4).map((u) => <img key={u} src={u} alt="" />)}
        {fotos.length > 4 && (
          <span className="compartilhado-mais">+{fotos.length - 4}</span>
        )}
      </div>

      <div className="pad">
        {abertos.map((c) => (
          <button key={c.id} className="desafio-card" onClick={() => escolher(c.id)}>
            <Marca desafio={c} />
            <div className="desafio-corpo">
              <div className="desafio-nome">{c.name}</div>
              <div className="desafio-estado">
                {c.memberUids.length} {c.memberUids.length === 1 ? "pessoa" : "pessoas"}
              </div>
            </div>
            <span className="desafio-chev"><IconChevron size={18} /></span>
          </button>
        ))}
      </div>
    </main>
  );
}
